/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React from "react"
import { Review } from "types/api/menuFeedback.type"
import { ActionType } from "types/models/feedback"
import { useTranslationsMap, useLocaleContext } from "common/utils/i18n"
import {
  DEFAULT_DATE_FORMAT,
  getDayjsLocale,
  getLocalizedDateFormat,
  TAIWAN_DATE_FORMAT,
} from "utils/i18n"
import { TroyWebTracker } from "utils/tracking"
import { EventCategory, EventAction } from "constants/tracking"
import dayjs from "dayjs"
import _isEmpty from "lodash/isEmpty"
import { canSubmitReply, isRoleAllowedReply } from "pages/feedback/utils"
import ShowMore from "components/common/ShowMore"

import ReplyContainer from "./ReplyContainer"

const style = css({
  maxWidth: "500px",
  ".reply-content": {
    fontWeight: 400,
    lineHeight: "18px",
  },
  ".reply-info": {
    marginTop: "4px !important",
    color: "#707070",
    fontSize: "11px",
  },
  ".btn-add-reply": {
    padding: 0,
    border: "none",
    background: "none",
    color: "#136fd8",
    marginTop: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    cursor: "pointer",
  },
})

type ReviewDetailsColumnProp = {
  review: Review
  onClick(actionType: ActionType, review: Review): void
}

const ReviewDetailsColumn: React.FC<ReviewDetailsColumnProp> = ({
  review,
  onClick,
}) => {
  const textMap = useTranslationsMap({
    reply: "feedback.cta.reply",
    repliedOn: "feedback.helper.repliedOn",
    editedOn: "feedback.helper.editedOn",
  })

  const locale = useLocaleContext("locale")
  const dayjsLocale = getDayjsLocale(locale)
  const dateFormat = getLocalizedDateFormat(
    locale,
    DEFAULT_DATE_FORMAT,
    TAIWAN_DATE_FORMAT,
  )

  const allowReply = isRoleAllowedReply() && canSubmitReply(review)

  let replyContent = null
  if (
    review.reviewReplies &&
    Array.isArray(review.reviewReplies) &&
    review.reviewReplies.length > 0
  ) {
    replyContent = review.reviewReplies[review.reviewReplies.length - 1]
  }

  const sendReplyEvent = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackHomePage,
      action: EventAction.clickReply,
      labels: {
        REVIEW_ID: review?.reviewID,
        REVIEW_RATING: review?.rating,
      },
    })
  }

  const handleAddReply = () => {
    sendReplyEvent()
    onClick(ActionType.ADD_REPLY, review)
  }

  const handleEditReply = () => {
    sendReplyEvent()
    onClick(ActionType.EDIT_REPLY, review)
  }

  const replyButton = allowReply ? (
    <button
      data-testid="btn-add-reply"
      className="btn-add-reply"
      type="button"
      onClick={handleAddReply}
    >
      {textMap.reply}
    </button>
  ) : null

  let replyStatusInfo = ""
  if (dayjs(replyContent?.createdAt).isBefore(dayjs(replyContent?.updatedAt))) {
    replyStatusInfo = textMap.editedOn.replace(
      /<date.short>/gi,
      `${dayjs(replyContent?.updatedAt)
        .locale(dayjsLocale)
        .format(dateFormat)}`,
    )
  } else {
    replyStatusInfo = textMap.repliedOn.replace(
      /<date.short>/gi,
      `${dayjs(replyContent?.createdAt)
        .locale(dayjsLocale)
        .format(dateFormat)}`,
    )
  }

  const replyUserInfo = !_isEmpty(replyContent?.repliedBy.trim())
    ? `by ${replyContent?.repliedBy}`
    : ""

  return (
    <div css={style}>
      <div className="reply-content">
        <ShowMore
          text={review.description}
          moreLabel="See more"
          lessLabel="See less"
        />
      </div>

      {replyContent ? (
        <React.Fragment>
          <ReplyContainer
            review={review}
            replyContent={replyContent}
            onClick={handleEditReply}
            data-testid="container-edit-reply"
          />
          <p className="reply-info">
            {replyStatusInfo} {replyUserInfo}
          </p>
        </React.Fragment>
      ) : (
        replyButton
      )}
    </div>
  )
}

export default React.memo(ReviewDetailsColumn)
